import { useEffect, useState } from "react";

import {
  ArrowDownward,
  ArrowUpward,
  Delete,
  Edit,
  Add,
  Save,
  Cancel,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import {
  getCrimeCodeRules,
  postCrimeCodeRule,
  patchCrimeCodeRule,
  deleteCrimeCodeRule,
  getOrgConfigPublic,
  CrimeCodeRuleModel,
  CrimeCodeRuleDefinition,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";

interface CrimeCodeRulesDialogProps {
  open: boolean;
  onClose: () => void;
  crimeCodeId: string;
  orgId: string;
}

type Operator =
  | "equals"
  | "contains"
  | "greater_than"
  | "less_than"
  | "in_list";

interface RuleFormData {
  field: string;
  operator: Operator;
  value: string;
  valueArray: string[];
  isDefault: boolean;
  surveyClass: string;
  priority: number;
}

const OPERATORS: { value: Operator; label: string }[] = [
  { value: "equals", label: "Equals" },
  { value: "contains", label: "Contains" },
  { value: "greater_than", label: "Greater Than" },
  { value: "less_than", label: "Less Than" },
  { value: "in_list", label: "In List" },
];

const COMMON_FIELDS = [
  "incident_type",
  "property_type",
  "value",
  "description",
  "location.address",
  "location.city",
];

const CrimeCodeRulesDialog: React.FC<CrimeCodeRulesDialogProps> = ({
  open,
  onClose,
  crimeCodeId,
  orgId,
}) => {
  const [rules, setRules] = useState<CrimeCodeRuleModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [surveyClasses, setSurveyClasses] = useState<string[]>([]);
  const [ruleForm, setRuleForm] = useState<RuleFormData>({
    field: "",
    operator: "equals",
    value: "",
    valueArray: [],
    isDefault: false,
    surveyClass: "",
    priority: 0,
  });

  useEffect(() => {
    if (open && crimeCodeId) {
      fetchRules();
      fetchSurveyClasses();
    }
  }, [open, crimeCodeId, orgId]);

  const fetchRules = async () => {
    if (!orgId || !crimeCodeId) return;
    try {
      setLoading(true);
      const response = await getCrimeCodeRules({
        path: { org_id: orgId, crime_code_id: crimeCodeId },
      });
      setRules(response.data || []);
    } catch (error) {
      console.error("Error fetching rules:", error);
      toast.error("Failed to load rules");
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyClasses = async () => {
    if (!orgId) return;
    try {
      const response = await getOrgConfigPublic({
        path: { org_id: orgId },
      });
      const classes = (response.data?.surveys || [])
        .filter((s) => !s.is_registry)
        .map((s) => s.survey_class)
        .filter((c): c is string => !!c);
      setSurveyClasses([...new Set(classes)]);
    } catch (error) {
      console.error("Error fetching survey classes:", error);
    }
  };

  const resetForm = () => {
    setRuleForm({
      field: "",
      operator: "equals",
      value: "",
      valueArray: [],
      isDefault: false,
      surveyClass: "",
      priority: Math.max(...rules.map((r) => r.priority), 0) + 1,
    });
    setEditingRuleId(null);
    setIsAdding(false);
  };

  const handleAdd = () => {
    resetForm();
    setIsAdding(true);
  };

  const handleEdit = (rule: CrimeCodeRuleModel) => {
    // For default rules, don't populate field/operator/value
    if (rule.is_default) {
      setRuleForm({
        field: "",
        operator: "equals",
        value: "",
        valueArray: [],
        isDefault: true,
        surveyClass: rule.survey_class || "",
        priority: rule.priority,
      });
    } else {
      const def = rule.rule_definition;
      const value = def.value;
      const isArray = Array.isArray(value);

      setRuleForm({
        field: def.field,
        operator: def.operator as Operator,
        value: isArray ? "" : String(value || ""),
        valueArray: isArray ? (value as string[]) : [],
        isDefault: false,
        surveyClass: rule.survey_class || "",
        priority: rule.priority,
      });
    }
    setEditingRuleId(rule.id);
    setIsAdding(false);
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleSave = async () => {
    // For default rules, field/operator/value are not required
    if (!ruleForm.isDefault && !ruleForm.field) {
      toast.error("Field is required for conditional rules");
      return;
    }

    try {
      // For default rules, use placeholder values since they're not evaluated
      let ruleDefinition: CrimeCodeRuleDefinition;
      if (ruleForm.isDefault) {
        ruleDefinition = {
          field: "_default",
          operator: "equals",
          value: true,
        };
      } else {
        const ruleValue =
          ruleForm.operator === "in_list"
            ? ruleForm.valueArray
            : ruleForm.operator === "greater_than" ||
                ruleForm.operator === "less_than"
              ? Number(ruleForm.value)
              : ruleForm.value;

        ruleDefinition = {
          field: ruleForm.field,
          operator: ruleForm.operator,
          value: ruleValue as any,
        };
      }

      if (editingRuleId) {
        await patchCrimeCodeRule({
          path: {
            org_id: orgId,
            crime_code_id: crimeCodeId,
            rule_id: editingRuleId,
          },
          body: {
            rule_definition: ruleDefinition,
            priority: ruleForm.priority,
            is_default: ruleForm.isDefault,
            survey_class: ruleForm.surveyClass || null,
          },
        });
        toast.success("Rule updated");
      } else {
        await postCrimeCodeRule({
          path: { org_id: orgId, crime_code_id: crimeCodeId },
          body: {
            rule_definition: ruleDefinition,
            priority: ruleForm.priority,
            is_default: ruleForm.isDefault,
            survey_class: ruleForm.surveyClass || null,
          },
        });
        toast.success("Rule created");
      }

      resetForm();
      fetchRules();
    } catch (error) {
      console.error("Error saving rule:", error);
      toast.error("Failed to save rule");
    }
  };

  const handleDelete = async (ruleId: string) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) {
      return;
    }

    try {
      await deleteCrimeCodeRule({
        path: {
          org_id: orgId,
          crime_code_id: crimeCodeId,
          rule_id: ruleId,
        },
      });
      toast.success("Rule deleted");
      fetchRules();
    } catch (error) {
      console.error("Error deleting rule:", error);
      toast.error("Failed to delete rule");
    }
  };

  const handleMovePriority = async (
    rule: CrimeCodeRuleModel,
    direction: "up" | "down",
  ) => {
    const currentIndex = rules.findIndex((r) => r.id === rule.id);
    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= rules.length) return;

    const targetRule = rules[newIndex];
    const newPriority = targetRule.priority;

    try {
      await patchCrimeCodeRule({
        path: {
          org_id: orgId,
          crime_code_id: crimeCodeId,
          rule_id: rule.id,
        },
        body: {
          priority: newPriority,
        },
      });

      await patchCrimeCodeRule({
        path: {
          org_id: orgId,
          crime_code_id: crimeCodeId,
          rule_id: targetRule.id,
        },
        body: {
          priority: rule.priority,
        },
      });

      fetchRules();
    } catch (error) {
      console.error("Error moving rule:", error);
      toast.error("Failed to reorder rule");
    }
  };

  const formatRuleDisplay = (rule: CrimeCodeRuleModel): string => {
    if (rule.is_default) {
      return "Default rule (applies when no conditional rules match)";
    }
    const def = rule.rule_definition;
    const valueStr =
      def.operator === "in_list" && Array.isArray(def.value)
        ? `[${def.value.join(", ")}]`
        : String(def.value);
    return `${def.field} ${def.operator} ${valueStr}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Manage Crime Code Rules</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            disabled={isAdding || editingRuleId !== null}
          >
            Add Rule
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        {(isAdding || editingRuleId) && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: "action.selected" }}>
            <Typography variant="subtitle1" gutterBottom>
              {editingRuleId ? "Edit Rule" : "Add New Rule"}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {!ruleForm.isDefault && (
                <>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Autocomplete
                      freeSolo
                      options={COMMON_FIELDS}
                      value={ruleForm.field}
                      onChange={(_event, newValue) =>
                        setRuleForm({ ...ruleForm, field: newValue || "" })
                      }
                      onInputChange={(_event, newValue) =>
                        setRuleForm({ ...ruleForm, field: newValue })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Field Path"
                          placeholder="e.g., incident_type or location.city"
                          helperText="Use dot notation for nested fields"
                          required
                        />
                      )}
                      sx={{ flex: 1 }}
                    />
                    <FormControl sx={{ minWidth: 150 }}>
                      <Select
                        value={ruleForm.operator}
                        onChange={(e) =>
                          setRuleForm({
                            ...ruleForm,
                            operator: e.target.value as Operator,
                            value: "",
                            valueArray: [],
                          })
                        }
                        label="Operator"
                      >
                        {OPERATORS.map((op) => (
                          <MenuItem key={op.value} value={op.value}>
                            {op.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {ruleForm.operator === "in_list" ? (
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={ruleForm.valueArray}
                      onChange={(_event, newValue) =>
                        setRuleForm({ ...ruleForm, valueArray: newValue })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Value List"
                          placeholder="Enter values, press Enter to add"
                          helperText="Add multiple values to check if field is in this list"
                        />
                      )}
                    />
                  ) : ruleForm.operator === "greater_than" ||
                    ruleForm.operator === "less_than" ? (
                    <TextField
                      type="number"
                      label="Value"
                      value={ruleForm.value}
                      onChange={(e) =>
                        setRuleForm({ ...ruleForm, value: e.target.value })
                      }
                      placeholder="Enter a number"
                      required
                    />
                  ) : (
                    <TextField
                      label="Value"
                      value={ruleForm.value}
                      onChange={(e) =>
                        setRuleForm({ ...ruleForm, value: e.target.value })
                      }
                      placeholder="Enter the value to match"
                      required
                    />
                  )}
                </>
              )}

              {ruleForm.isDefault && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "info.light",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "info.main",
                  }}
                >
                  <Typography variant="body2" color="info.dark">
                    Default rules are applied when no conditional rules match.
                    This crime code will be automatically assigned to the
                    selected report types (or all types if none selected) when
                    no other rules apply.
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <Select
                    value={ruleForm.surveyClass}
                    onChange={(e) =>
                      setRuleForm({ ...ruleForm, surveyClass: e.target.value })
                    }
                    displayEmpty
                  >
                    <MenuItem value="">All Report Types</MenuItem>
                    {surveyClasses.map((sc) => (
                      <MenuItem key={sc} value={sc}>
                        {sc}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  type="number"
                  label="Priority"
                  value={ruleForm.priority}
                  onChange={(e) =>
                    setRuleForm({
                      ...ruleForm,
                      priority: parseInt(e.target.value) || 0,
                    })
                  }
                  inputProps={{ min: 0 }}
                  sx={{ width: 120 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ruleForm.isDefault}
                      onChange={(e) => {
                        const isDefault = e.target.checked;
                        setRuleForm({
                          ...ruleForm,
                          isDefault,
                          // Clear field/operator/value when switching to default
                          field: isDefault ? "" : ruleForm.field,
                          operator: isDefault ? "equals" : ruleForm.operator,
                          value: isDefault ? "" : ruleForm.value,
                          valueArray: isDefault ? [] : ruleForm.valueArray,
                        });
                      }}
                    />
                  }
                  label="Default Rule (applies when no other rules match)"
                />
              </Box>

              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  startIcon={<Save />}
                  onClick={handleSave}
                  variant="contained"
                >
                  Save Rule
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={60}>Priority</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell>Report Type</TableCell>
                <TableCell>Type</TableCell>
                <TableCell width={200}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules
                .sort((a, b) => a.priority - b.priority)
                .map((rule, index) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleMovePriority(rule, "up")}
                          disabled={index === 0}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <Typography variant="body2">{rule.priority}</Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleMovePriority(rule, "down")}
                          disabled={index === rules.length - 1}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatRuleDisplay(rule)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {rule.survey_class ? (
                        <Chip label={rule.survey_class} size="small" />
                      ) : (
                        <Chip label="All" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      {rule.is_default ? (
                        <Chip label="Default" size="small" color="primary" />
                      ) : (
                        <Chip label="Conditional" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(rule)}
                        disabled={isAdding || editingRuleId !== null}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(rule.id)}
                        disabled={isAdding || editingRuleId !== null}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              {rules.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No rules defined. Add a rule to automatically assign this
                      crime code.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrimeCodeRulesDialog;
